import { __awaiter } from "tslib";
import * as React from 'react';
import { memo, useCallback, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Collapse from '@mui/material/Collapse';
import { useField } from 'react-final-form';
import { FormControlLabel, Checkbox } from '@mui/material';
import Input from '../../Input';
import { MOBILE_MIN_WIDTH } from '../../utils';
import PhoneInput from '../../PhoneInput';
import { BaseButton } from '../../BaseComponents';
import SocialAuth from '../../SocialAuth';
import { defaultTheme, Provider } from '../../SocialAuth/theme';
import LoginBlock from './LoginBlock/LoginBlock';
import { useTheme } from '../../theme';
import { InputType } from '../../Passenger/types';
import { hideText, validate } from '../../Passenger/utils';
import MediaQuery from '../../MediaQuery/MediaQuery';
import FieldsOrder from './FieldsOrder/FieldsOrder';
import { useMediaQuery } from 'react-responsive';
import { LoginType, useAccountExistenceLazyQuery } from '@websky/graphql';
import { CdnIcon, SafeHtml } from '@shared/ui';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
let debounceTimeout = 0;
export var UserExistStatus;
(function (UserExistStatus) {
    UserExistStatus["Login"] = "login";
    UserExistStatus["Register"] = "register";
})(UserExistStatus || (UserExistStatus = {}));
const Contacts = memo(props => {
    const theme = useTheme('Contacts').ContactsStyles;
    const [userExistInfo, setUserExistInfo] = useState(null);
    const { t } = useTranslation('Contacts');
    const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
    const escapedT = useCallback((label) => typeof label !== 'undefined'
        ? t(label, {
            // This is necessary to avoid colon problems in error messages.
            nsSeparator: '|',
            keySeparator: '>'
        })
        : label, []);
    const [checkAccountExistence, { data: accountExistenceData, loading: isAccountExistenceLoading, variables: accountExistenceVars }] = useAccountExistenceLazyQuery();
    const updateUserExistenceState = (accountExistenceData) => {
        var _a, _b;
        const confirmedLoginTypes = new Set((userExistInfo === null || userExistInfo === void 0 ? void 0 : userExistInfo.confirmedLoginTypes) || []);
        const paramsLoginType = (_a = accountExistenceVars === null || accountExistenceVars === void 0 ? void 0 : accountExistenceVars.params) === null || _a === void 0 ? void 0 : _a.loginType;
        // Если аккаунт найден, добавляем текущее поле в сет подтвержденных
        if ((_b = accountExistenceData === null || accountExistenceData === void 0 ? void 0 : accountExistenceData.AccountExistence) === null || _b === void 0 ? void 0 : _b.result) {
            confirmedLoginTypes.add(paramsLoginType);
            setUserExistInfo({ status: UserExistStatus.Login, confirmedLoginTypes });
            return;
        }
        // Если аккаунт не найден, но найден посредством другого поля, удаляем текущее поле из сета подтвержденных
        if ((userExistInfo === null || userExistInfo === void 0 ? void 0 : userExistInfo.status) === UserExistStatus.Login &&
            Array.from((userExistInfo === null || userExistInfo === void 0 ? void 0 : userExistInfo.confirmedLoginTypes) || []).some(value => value !== paramsLoginType)) {
            confirmedLoginTypes.delete(paramsLoginType);
            setUserExistInfo({ status: UserExistStatus.Login, confirmedLoginTypes });
            return;
        }
        setUserExistInfo({ status: UserExistStatus.Register });
    };
    React.useEffect(() => {
        if (accountExistenceData) {
            updateUserExistenceState(accountExistenceData);
        }
    }, [accountExistenceData]);
    const phone = useField(props.phone.name, {
        type: props.phone.input,
        initialValue: props.phone.defaultValue,
        validate: value => escapedT(validate(value, {}, InputType.Phone, props.phone.validations))
    });
    const email = useField(props.email.name, {
        type: props.email.input,
        initialValue: props.email.defaultValue,
        validate: value => escapedT(validate(value, {}, InputType.Text, props.email.validations))
    });
    const onLoginOpenHandler = () => {
        var _a;
        // Если почта подтверждена, то берем ее, иначе другое подтвержденное поле
        const loginType = ((_a = userExistInfo === null || userExistInfo === void 0 ? void 0 : userExistInfo.confirmedLoginTypes) === null || _a === void 0 ? void 0 : _a.has(LoginType.Email))
            ? LoginType.Email
            : Array.from((userExistInfo === null || userExistInfo === void 0 ? void 0 : userExistInfo.confirmedLoginTypes) || [])[0] || props.loginType;
        props.onLoginOpen(loginType === LoginType.Email ? email.input.value : loginType === LoginType.Phone ? phone.input.value : null);
    };
    const subscribed = useField('subscribed', {
        type: 'checkbox',
        defaultValue: false
    });
    const onChangeHandler = useCallback((event, loginType, isValid) => {
        if (!event) {
            return;
        }
        const value = typeof event === 'string' ? event : event.target.value;
        if (props.open && !props.isAuthorized && isValid) {
            clearTimeout(debounceTimeout);
            debounceTimeout = window.setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
                checkAccountExistence({
                    variables: {
                        params: {
                            login: value,
                            loginType
                        }
                    }
                });
            }), 500);
        }
        if (loginType === LoginType.Email) {
            email.input.onChange(event);
        }
        if (loginType === LoginType.Phone) {
            phone.input.onChange(event);
        }
    }, [accountExistenceData, userExistInfo]);
    const userInfo = [];
    if (email.input.value) {
        userInfo.push(hideText(email.input.value));
    }
    if (phone.input.value) {
        userInfo.push(formatPhoneNumberIntl(`+${phone.input.value}`));
    }
    const inputVariant = props.variant ? props.variant : isMobile ? 'standard' : 'filled';
    const hasContactsError = email.meta.invalid || phone.meta.invalid;
    return (React.createElement("div", { className: cn(theme.contacts, { [theme.short]: !props.open }) },
        React.createElement("div", { className: theme.header },
            React.createElement("div", { className: theme.description__header },
                React.createElement("div", { className: theme.icon_notifications },
                    React.createElement(CdnIcon, { icon: "Info/Notifications" })),
                t('Your contacts'),
                ' ',
                !hasContactsError && React.createElement(CdnIcon, { className: theme.icon_success, icon: "Info/Item Icluded" })),
            !props.open && (React.createElement("div", { className: theme.customer__info },
                React.createElement("div", { className: cn(theme.field) }, userInfo.join(', '))))),
        props.open && (React.createElement(React.Fragment, null,
            React.createElement("div", { className: theme.form__container },
                React.createElement("div", { className: theme.form },
                    React.createElement("div", { className: theme.description },
                        React.createElement("div", { className: theme.description__text },
                            React.createElement(CdnIcon, { icon: "Info/Warning" }),
                            React.createElement(SafeHtml, { tag: "div", className: theme.description__content, html: t('Contact details are necessary for us to be able to pass on the latest flight information.') }))),
                    React.createElement("div", { className: theme.fields },
                        React.createElement(FieldsOrder, { reverse: props.loginType === LoginType.Email },
                            React.createElement("div", { className: theme.field },
                                React.createElement(PhoneInput, { disabled: props.isInputsReadonly, className: theme.field__contact, value: phone.input.value, textFieldsProps: Object.assign(Object.assign({}, phone.input), { variant: inputVariant, disabled: props.isInputsReadonly, label: t('Phone'), type: 'tel', helperText: ((phone.meta.modified && phone.meta.touched) ||
                                            phone.meta.submitFailed) &&
                                            (phone.meta.error || phone.meta.submitError), error: ((phone.meta.modified && phone.meta.touched) ||
                                            phone.meta.submitFailed) &&
                                            (!!phone.meta.error || !!phone.meta.submitError), onChange: e => onChangeHandler((e || '').replace(/\+/g, ''), LoginType.Phone, !validate(e, {}, InputType.Phone, props.phone.validations)) }) })),
                            React.createElement("div", { className: theme.field },
                                React.createElement(Input, { variant: inputVariant, absoluteHintPosition: true, inputClassName: theme.field__contact, disabled: props.isInputsReadonly, autoFocus: props.loginType === LoginType.Email && props.autofocus, TextFieldProps: Object.assign(Object.assign({}, email.input), { value: email.input.value !== 'null' ? email.input.value : '', label: t('Email'), type: 'email', helperText: ((email.meta.modified && email.meta.touched) ||
                                            email.meta.submitFailed) &&
                                            (email.meta.error || email.meta.submitError), error: ((email.meta.modified && email.meta.touched) ||
                                            email.meta.submitFailed) &&
                                            (!!email.meta.error || !!email.meta.submitError), onChange: e => onChangeHandler(e, LoginType.Email, !validate(e.target.value, {}, InputType.Text, props.email.validations)) }) }))))),
                React.createElement(Collapse, { mountOnEnter: true, unmountOnExit: true, in: !props.isAuthorized
                        ? !!userExistInfo && props.showSubscribeCheckbox
                        : props.showSubscribeCheckbox },
                    React.createElement("div", { className: theme.additional },
                        React.createElement(FormControlLabel, { classes: {
                                label: theme.checkbox__label
                            }, label: t('Receive a mailing list with promotional offers and discounts'), control: React.createElement(Checkbox, { color: "primary", classes: {
                                    root: theme.checkbox,
                                    checked: theme.checkbox_checked
                                }, checked: subscribed.input.checked, inputProps: subscribed.input }) }))),
                React.createElement(Collapse, { mountOnEnter: true, unmountOnExit: true, in: !props.isAuthorized },
                    React.createElement("div", { className: cn(theme.footer, theme.footer_login) },
                        React.createElement(MediaQuery, { maxWidth: "mobile" },
                            React.createElement(CdnIcon, { className: theme.footer__timerIcon, icon: "Info/Timer" })),
                        React.createElement("div", { className: theme.footer__inner },
                            React.createElement("div", { className: theme.footer__text },
                                React.createElement(MediaQuery, { minWidth: "mobile" }, t((userExistInfo === null || userExistInfo === void 0 ? void 0 : userExistInfo.status) === UserExistStatus.Login
                                    ? 'Want to speed up passenger data entry? Log in and continue.'
                                    : 'Want to speed up passenger data entry? Sign up and continue.')),
                                React.createElement(MediaQuery, { maxWidth: "mobile" },
                                    React.createElement("span", null, t('Want to speed up passenger data entry?')))),
                            React.createElement(BaseButton, { variant: "optionAction", onClick: onLoginOpenHandler, isLoading: isAccountExistenceLoading }, t((userExistInfo === null || userExistInfo === void 0 ? void 0 : userExistInfo.status) === UserExistStatus.Login ? 'Log in' : 'Sign up'))),
                        React.createElement(MediaQuery, { minWidth: "mobile" },
                            React.createElement("div", { className: theme.footer__social },
                                React.createElement(Provider, { value: {
                                        SocialAuth: Object.assign(Object.assign({}, defaultTheme.SocialAuth), { button: theme.footer__social__button })
                                    } },
                                    React.createElement(SocialAuth, null))))))))),
        React.createElement(Collapse, { in: props.isFormLocked, unmountOnExit: true },
            React.createElement(LoginBlock, { onClick: onLoginOpenHandler, onSkipClick: props.unlockForm }))));
});
export default Contacts;
